/**
 * This class manages adding and removing global (window) event handler's for touch/mouse move and up and capturing
 * the events if need be (on IE).
 *
 * This is useful for implementing drag behavior.  If you rely on element level move and up events, you will only
 * get those if the user's mouse pointer or finger stayed within the bounds of the element.  As soon as they move
 * outside the element (or outside the browser) you won't get them.
 */
export class GlobalPointerEventCapturer {
  private listenersAdded: boolean;
  private elementRef: HTMLElement;
  private onPointerMove: (e: MouseEvent | TouchEvent) => void;
  private onPointerUp: (e: MouseEvent | TouchEvent) => void;

  public constructor(
    // We need this elementRef solely for IE, see comments about set and release capture below
    elementRef: HTMLElement,
    onPointerMove: (e: MouseEvent | TouchEvent) => void,
    onPointerUp: (e: MouseEvent | TouchEvent) => void
  ) {
    this.listenersAdded = false;
    this.elementRef = elementRef;
    this.onPointerMove = onPointerMove;
    this.onPointerUp = onPointerUp;
  }

  public addListeners(): void {
    if (this.listenersAdded) {
      return;
    }

    window.addEventListener('touchend', this.onPointerUp, true);
    window.addEventListener('mouseup', this.onPointerUp, true);
    if (window !== window.top) { // TFSID 1265762: release capture if mousing out of an iframe
      document.addEventListener('touchcancel', this.onPointerUp, true);
      document.addEventListener('mouseleave', this.onPointerUp);
    }
    // `passive: false` necessary for TFSID 798261 + https://bugs.webkit.org/show_bug.cgi?id=182521
    window.addEventListener('touchmove', this.onPointerMove, { capture: true, passive: false });
    window.addEventListener('mousemove', this.onPointerMove, { capture: true, passive: false });

    // On IE we have to setCapture the element so that if the mouse goes outside the bounds of the page, we will still
    // get mousemove and mouseup events.
    // tslint:disable-next-line:no-any
    if ((this.elementRef as any).setCapture) {
      // tslint:disable-next-line:no-any
      (this.elementRef as any).setCapture();
    }

    this.listenersAdded = true;
  }

  public removeListeners(): void {
    if (!this.listenersAdded) {
      return;
    }

    window.removeEventListener('touchend', this.onPointerUp, true);
    window.removeEventListener('mouseup', this.onPointerUp, true);
    document.removeEventListener('touchcancel', this.onPointerUp, true);
    document.removeEventListener('mouseleave', this.onPointerUp);
    window.removeEventListener('touchmove', this.onPointerMove, { capture: true });
    window.removeEventListener('mousemove', this.onPointerMove, { capture: true });

    // We have to releaseCapture on IE since we setCapture there
    // tslint:disable-next-line:no-any
    if ((this.elementRef as any).releaseCapture) {
      // tslint:disable-next-line:no-any
      (this.elementRef as any).releaseCapture();
    }

    this.listenersAdded = false;
  }
}
